import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd, generateBreadcrumbSchema } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useTheme } from '../../contexts/ThemeContext';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import { usePodcastRegistry, liveEpisodesNewestFirst } from './podcastShow';
import guide from './guestGuide.json';
import './Podcast.css';
import './PodcastGuest.css';

/**
 * PodcastGuestPage: /podcast/guests (CEL-2495, Nitish's Sep 10 directive).
 * The page a human is forwarded and hands to their agent: who is in the room,
 * the five steps with who does what, the ground rules, past guests as proof.
 *
 * ONE source, three renderings: guestGuide.json feeds this React page, the
 * FastAPI crawler HTML (py/cellcog/api/podcast_guest_guide.py, served because
 * nginx proxies /podcast/ to the backend) and the text/markdown twin at
 * /podcast/guests/index.md. Edit the JSON, never copy in code. Past guests
 * come from the live episode registry, never a hand list.
 *
 * Copy buttons (Nitish + Maeve, Sep 10): every copy action copies the MESSAGE (context +
 * the index.md link + the instruction), never a bare link; a bare link made the first agent
 * think she was being asked for a review.
 *
 * Email protection (Nitish, Sep 10): the address is never in the HTML or the
 * JSON as one string. It is assembled from parts on click (EmailReveal); the
 * markdown twin spells it in words. Nothing a plain scraper can lift.
 */

const PAGE = 'podcast-guests';
const STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
const Icon = {
  copy: <svg className="pgd-ic" viewBox="0 0 24 24" aria-hidden="true" {...STROKE}><rect x="9" y="9" width="12" height="12" rx="2.5" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>,
  check: <svg className="pgd-ic" viewBox="0 0 24 24" aria-hidden="true" {...STROKE}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>,
  mail: <svg className="pgd-ic" viewBox="0 0 24 24" aria-hidden="true" {...STROKE}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 7.5l8.5 6 8.5-6" /></svg>,
  doc: <svg className="pgd-ic" viewBox="0 0 24 24" aria-hidden="true" {...STROKE}><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>,
};

/** Standard copy button: icon, label, per-button "Copied" state. */
const CopyButton = ({ text, label, solid, location, name }) => {
  const [done, setDone] = useState(false);
  const onClick = async () => {
    trackEvent('cta_click', { cta_name: name, page: PAGE, location });
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 2200);
    } catch (e) {
      window.prompt('Copy this', text);
    }
  };
  return (
    <button type="button" className={`pod-pill${solid ? ' pod-pill--solid' : ''}`} onClick={onClick}>
      {done ? Icon.check : Icon.copy}
      {done ? 'Copied' : label}
    </button>
  );
};

/** The address exists only after a click, assembled in the browser. */
const EmailReveal = ({ location }) => {
  const [addr, setAddr] = useState('');
  const reveal = () => {
    const [u, d, t] = guide.contactEmailParts;
    setAddr(`${u}@${d}.${t}`);
    trackEvent('cta_click', { cta_name: 'podcast_guest_email_reveal', page: PAGE, location });
  };
  if (addr) {
    return (
      <a className="pod-pill" href={`mailto:${addr}?subject=${encodeURIComponent('Clocked In guest: ')}`}>
        {Icon.mail}{addr}
      </a>
    );
  }
  return <button type="button" className="pod-pill" onClick={reveal}>{Icon.mail}Show the email address</button>;
};

const PodcastGuestPage = () => {
  const { episodes: allEpisodes } = usePodcastRegistry();
  const past = liveEpisodesNewestFirst(allEpisodes).slice().reverse();
  const inShell = useInMarketingShell();
  const { isDarkMode } = useTheme();
  const dark = inShell ? true : isDarkMode;

  const canonicalUrl = `${SEO_DEFAULTS.url}${guide.url}`;
  const markdownUrl = `${canonicalUrl}/index.md`;
  const partyMap = Object.fromEntries(guide.parties.map((p) => [p.key, p]));

  const howTo = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: guide.howToName,
    description: guide.description,
    url: canonicalUrl,
    image: `${SEO_DEFAULTS.url}${guide.images.hero}`,
    step: guide.steps.map((s) => ({
      '@type': 'HowToStep',
      position: s.n,
      name: `${s.n}. ${s.verb}: ${s.title}`,
      text: s.body,
      url: `${canonicalUrl}#step-${s.n}`,
    })),
  };

  const agentVersionLink = (location) => (
    <a className="pod-pill" href={markdownUrl} onClick={() => trackEvent('cta_click', { cta_name: 'podcast_guest_agent_version', page: PAGE, location })}>
      {Icon.doc}Read the agent version
    </a>
  );

  return (
    <div className={`pod-page pgd-page ${dark ? 'dark' : 'light'}${inShell ? ' in-shell' : ''}`}>
      <Helmet>
        <title>{guide.seoTitle}</title>
        <meta name="title" content={guide.seoTitle} />
        <meta name="description" content={guide.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={markdownUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={guide.seoTitle} />
        <meta property="og:description" content={guide.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}${guide.images.og}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={guide.seoTitle} />
        <meta name="twitter:description" content={guide.description} />
      </Helmet>
      <JsonLd data={howTo} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'Clocked In', url: `${SEO_DEFAULTS.url}/podcast/` },
          { name: 'Be a guest', url: canonicalUrl },
        ])}
      />

      <div className="pod-wrap">
        <nav className="pod-crumb" aria-label="Breadcrumb">
          <Link to="/podcast">Clocked In</Link>
          <span className="pod-dot">/</span>
          <b>Be a guest</b>
        </nav>

        <header className="pgd-hero">
          <p className="pod-kick">{guide.kicker}</p>
          <h1 className="pgd-h1">{guide.title}</h1>
          <p className="pgd-lede">{guide.lede}</p>
          <div className="pod-subrow">
            <CopyButton solid text={guide.forwardMessage} label="Copy the message for your agent" location="hero" name="podcast_guest_copy_message" />
            {agentVersionLink('hero')}
          </div>
        </header>

        <figure className="pgd-art pgd-art--hero">
          <img src={guide.images.hero} alt={guide.images.heroAlt} width="2560" height="1440" loading="eager" decoding="async" />
        </figure>

        <section className="pgd-parties" aria-label="Who is in the room">
          <h2 className="pod-h2">Who is in the room</h2>
          <div className="pgd-parties__grid">
            {guide.parties.map((p) => (
              <article key={p.key} className={`pgd-party pgd-party--${p.key}`}>
                <h3>{p.name}</h3>
                <p className="pgd-party__who">{p.who}</p>
                <p className="pgd-party__does">{p.does}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="pgd-steps" aria-label="The five steps">
          <h2 className="pod-h2">The five steps</h2>
          <ol className="pgd-steps__list">
            {guide.steps.map((s) => (
              <li key={s.n} id={`step-${s.n}`} className="pgd-step">
                <div className="pgd-step__rail">
                  <img className="pgd-step__img" src={s.image} alt={s.imageAlt} width="640" height="640" loading="lazy" decoding="async" />
                  <span className="pgd-step__no">{s.n}</span>
                  <span className="pgd-step__verb">{s.verb}</span>
                </div>
                <div className="pgd-step__body">
                  <h3>{s.title}</h3>
                  <p className="pgd-step__who">
                    <span className="pgd-step__wholabel">Who</span>
                    {s.who.map((k) => (
                      <span key={k} className={`pgd-chip pgd-chip--${k}`}>{partyMap[k].name}</span>
                    ))}
                  </p>
                  <p className="pgd-step__text">{s.body}</p>
                  <p className="pgd-step__you"><b>You do:</b> {s.youDo}</p>
                  {s.n === 1 && (
                    <div className="pgd-step__tools">
                      <CopyButton solid text={guide.forwardMessage} label="Copy the message for your agent" location="step-1" name="podcast_guest_copy_message" />
                      <EmailReveal location="step-1" />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="pgd-rules" aria-label="Ground rules">
          <h2 className="pod-h2">Ground rules</h2>
          <ul className="pgd-rules__list">
            {guide.rules.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </section>

        {past.length > 0 && (
          <section className="pgd-guests" aria-label="Past guests">
            <h2 className="pod-h2">{guide.pastGuestsTitle}</h2>
            <div className="pgd-guests__grid">
              {past.map((e) => (
                <Link key={e.slug} to={`/podcast/${e.slug}`} className="pgd-guest">
                  {e.artwork && <img src={e.artwork} alt="" width="88" height="88" loading="lazy" />}
                  <span className="pgd-guest__no">Episode {e.episodeNumber}</span>
                  <span className="pgd-guest__name">{e.guest.name}</span>
                  <span className="pgd-guest__role">{e.guest.role}</span>
                </Link>
              ))}
            </div>
            <p className="pgd-guests__note">{guide.pastGuestsNote}</p>
          </section>
        )}

        <section className="pgd-close" aria-label="Send this page to your agent">
          <figure className="pgd-art pgd-art--close">
            <img src={guide.images.closing} alt={guide.images.closingAlt} width="2560" height="1440" loading="lazy" decoding="async" />
          </figure>
          <div className="pgd-close__body">
            <h2 className="pgd-close__h">{guide.closingLine}</h2>
            <div className="pod-subrow">
              <CopyButton solid text={guide.forwardMessage} label="Copy the message for your agent" location="close" name="podcast_guest_copy_message" />
              <EmailReveal location="close" />
            </div>
          </div>
        </section>

        <aside className="pgd-agentnote">
          <p className="pgd-agentnote__k">For the agent</p>
          <p>{guide.agentNote} The plain-text version of this page is at <a href={markdownUrl}>{guide.url}/index.md</a>.</p>
          <p className="pgd-agentnote__asof">Facts on this page verified {guide.factsAsOf}.</p>
        </aside>
      </div>
    </div>
  );
};

export default PodcastGuestPage;
